// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type {
  Composition,
  CompositionSection,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
} from '@medplum/fhirtypes';
import { Loading, useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import { CustomQuestionnaireResponseDisplay } from '../../components/tasks/encounter/CustomQuestionnaireResponseDisplay';

const UPLOAD_LINK_IDS: Record<string, { questionnaireName: string; title: string }> = {
  'stopbang-upload': { questionnaireName: 'stop-bang', title: 'STOP-BANG' },
  'ess-upload': { questionnaireName: 'epworth-sleepiness-scale', title: 'Epworth Sleepiness Scale' },
};

// Maps a set of distinctive linkIds to the questionnaire name they identify.
// Used to inject a questionnaire reference into QRs that were saved without one.
// Order matters: more specific hints (longer linkId lists) should come first.
// NOTE: linkIds must match public/public-questionnaires.json, not data/questionnaires.json
const QUESTIONNAIRE_LINKID_HINTS: { linkIds: string[]; name: string }[] = [
  { linkIds: ['diagnoses', 'clinical-conditions'], name: 'eligibility-check-page-1' },
  { linkIds: ['previous-sleep-study', 'has-completed-stopbang'], name: 'pcp-referral-triage' },
  { linkIds: ['snore-loudly', 'tired-fatigued'], name: 'stop-bang' }, // new linkIds from merged sleep-history
  { linkIds: ['ess-reading', 'ess-television'], name: 'epworth-sleepiness-scale' }, // new linkIds from merged sleep-history
  { linkIds: ['snore', 'tired'], name: 'stop-bang' }, // fallback: old linkIds from data/questionnaires.json
  { linkIds: ['1', '2'], name: 'epworth-sleepiness-scale' }, // fallback: old linkIds from data/questionnaires.json
  { linkIds: ['insurance-provider'], name: 'pcp-referral-patient-info' },
  { linkIds: ['provider-first-name', 'provider-last-name'], name: 'pcp-referral-provider-info' },
];

function collectLinkIds(items: QuestionnaireResponseItem[] | undefined, out: Set<string>): void {
  for (const item of items ?? []) {
    if (item.linkId) {
      out.add(item.linkId);
    }
    collectLinkIds(item.item, out);
  }
}

function detectQuestionnaireName(qr: QuestionnaireResponse): string | undefined {
  const linkIds = new Set<string>();
  collectLinkIds(qr.item, linkIds);
  return QUESTIONNAIRE_LINKID_HINTS.find(({ linkIds: hints }) => hints.every((id) => linkIds.has(id)))?.name;
}

export function CompositionReviewPage(): JSX.Element {
  const { compositionId } = useParams();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [composition, setComposition] = useState<Composition>();
  const [questionnaireResponses, setQuestionnaireResponses] = useState<QuestionnaireResponse[]>([]);
  const [answeredQuestionnaires, setAnsweredQuestionnaires] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(!!compositionId);

  useEffect(() => {
    if (!compositionId) {
      return;
    }

    medplum
      .readResource('Composition', compositionId)
      .then(async (comp) => {
        setComposition(comp);

        const qrRefs = (comp.section ?? [])
          .flatMap((section: CompositionSection) => section.entry ?? [])
          .filter((entry: Reference) => entry.reference?.startsWith('QuestionnaireResponse/'));

        const qrs: QuestionnaireResponse[] = await Promise.all(
          qrRefs.map((entry: Reference) => {
            const id = (entry.reference ?? '').replace('QuestionnaireResponse/', '');
            return medplum.readResource('QuestionnaireResponse', id);
          })
        );

        // Always look up IDs for upload questionnaires so we can detect them
        // by their `questionnaire` reference, not just by linkId hints.
        const uploadQNames = [...new Set(Object.values(UPLOAD_LINK_IDS).map((v) => v.questionnaireName))];

        // For QRs missing a questionnaire reference, detect and inject it so
        // QuestionnaireResponseDisplay can fetch the question text.
        const detectedNames = new Set([
          ...(qrs
            .filter((qr: QuestionnaireResponse) => !qr.questionnaire)
            .map(detectQuestionnaireName)
            .filter(Boolean) as string[]),
          ...uploadQNames,
        ]);

        const nameToId = new Map<string, string>();
        await Promise.all(
          Array.from(detectedNames).map(async (name) => {
            const q = await medplum.searchOne('Questionnaire', { name });
            if (q?.id) {
              nameToId.set(name, q.id);
            }
          })
        );

        const idToName = new Map([...nameToId].map(([name, id]) => [id, name]));
        const answered = new Set<string>();
        for (const qr of qrs) {
          if (qr.questionnaire) {
            const qId = qr.questionnaire.replace('Questionnaire/', '');
            const name = idToName.get(qId);
            if (name) {
              answered.add(name);
            }
          }
          const detected = detectQuestionnaireName(qr);
          if (detected) {
            answered.add(detected);
          }
        }
        setAnsweredQuestionnaires(answered);

        const enriched = qrs.map((qr: QuestionnaireResponse) => {
          if (qr.questionnaire) {
            return qr;
          }
          const name = detectQuestionnaireName(qr);
          const qId = name ? nameToId.get(name) : undefined;
          return qId ? { ...qr, questionnaire: `Questionnaire/${qId}` } : qr;
        });

        setQuestionnaireResponses(enriched);
      })
      .catch((error) => {
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      })
      .finally(() => setLoading(false));
  }, [medplum, compositionId]);

  if (loading) {
    return <Loading />;
  }

  if (!composition) {
    return (
      <Stack p="md">
        <Title order={2}>Composition not found</Title>
      </Stack>
    );
  }

  const REQUIRED_PCP_QUESTIONNAIRES = [
    { name: 'stop-bang', label: 'STOP-BANG' },
    { name: 'epworth-sleepiness-scale', label: 'Epworth Sleepiness Scale' },
  ];

  const isPcpReferral = [...answeredQuestionnaires].some((name) => name.startsWith('pcp-referral-'));
  const missingQuestionnaires = isPcpReferral
    ? REQUIRED_PCP_QUESTIONNAIRES.filter(({ name }) => !answeredQuestionnaires.has(name))
    : [];

  return (
    <Stack gap="md" p="md">
      {missingQuestionnaires.length > 0 && (
        <Alert color="red" title="Manual review required">
          <Group justify="space-between" align="center">
            <Text size="sm">
              The following questionnaires must be filled before triage algorithm can be applied:{' '}
              <strong>{missingQuestionnaires.map((q) => q.label).join(' and ')}</strong>.
            </Text>
            <Button
              color="red"
              size="sm"
              onClick={() =>
                navigate(
                  `/triage/composition/${compositionId}/questionnaires?q=${missingQuestionnaires.map((q) => q.name).join(',')}`
                )
              }
            >
              Fill questionnaires
            </Button>
          </Group>
        </Alert>
      )}
      <PageHeader
        title={composition.title ?? 'Intake Review'}
        actions={
          <Button variant="subtle" onClick={() => navigate('/triage')}>
            Back to Triage
          </Button>
        }
      />

      {questionnaireResponses.length === 0 ? (
        <Text c="dimmed">No questionnaire responses found in this composition.</Text>
      ) : (
        questionnaireResponses.map((qr) => (
          <Paper key={qr.id} withBorder radius="md" shadow="sm" p="md">
            <CustomQuestionnaireResponseDisplay questionnaireResponse={qr} />
          </Paper>
        ))
      )}
    </Stack>
  );
}
